import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Calendar } from 'lucide-react';

const TIMEFRAME_OPTIONS = [
  { label: 'Past 90 days', value: 90 },
  { label: 'Past 60 days', value: 60 },
  { label: 'Past 30 days', value: 30 },
  { label: 'Past 7 days', value: 7 },
  { label: 'Past 24 hours', value: 1 },
];

export default function TimeframeSelectorPopover({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTemp, setSelectedTemp] = useState(value || 7);
  const popoverRef = useRef(null);

  // Sync temp value when props change
  useEffect(() => {
    setSelectedTemp(value);
  }, [value]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const currentOption = TIMEFRAME_OPTIONS.find((o) => o.value === value) || TIMEFRAME_OPTIONS[3];

  const handleApply = () => {
    if (onChange) {
      onChange(selectedTemp);
    }
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block text-left" ref={popoverRef}>
      {/* Trigger Button */}
      <button
        type="button"
        data-testid="date-range-selector"
        onClick={() => setIsOpen(!isOpen)}
        className="min-h-11 px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium flex items-center gap-2 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FEAB3D]"
      >
        <Calendar className="w-4 h-4 text-slate-400 dark:text-slate-400" />
        <span>{currentOption.label}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Popover Menu */}
      {isOpen && (
        <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-64 max-w-[calc(100vw-2rem)] bg-white dark:bg-[#061727] border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-5 z-50 animate-in fade-in zoom-in-95 duration-150">
          <div className="space-y-3.5 mb-5">
            {TIMEFRAME_OPTIONS.map((opt) => {
              const isSelected = selectedTemp === opt.value;
              return (
                <label
                  key={opt.value}
                  onClick={() => setSelectedTemp(opt.value)}
                  className="flex items-center gap-3 cursor-pointer group py-0.5 select-none"
                >
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                      isSelected
                        ? 'border-[#021426] dark:border-[#FEAB3D]'
                        : 'border-slate-300 dark:border-slate-600 group-hover:border-slate-400'
                    }`}
                  >
                    {isSelected && (
                      <div className="w-2.5 h-2.5 rounded-full bg-[#021426] dark:bg-[#FEAB3D]" />
                    )}
                  </div>
                  <span
                    className={`text-sm font-medium transition-colors ${
                      isSelected
                        ? 'text-slate-900 dark:text-white font-semibold'
                        : 'text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white'
                    }`}
                  >
                    {opt.label}
                  </span>
                </label>
              );
            })}
          </div>

          {/* Apply Button */}
          <button
            type="button"
            onClick={handleApply}
            className="w-full bg-[#1c1917] dark:bg-[#FEAB3D] text-white dark:text-[#021426] font-bold py-2.5 rounded-xl hover:opacity-90 active:scale-[0.98] transition-all shadow-md text-sm tracking-wide"
          >
            Apply time frame
          </button>
        </div>
      )}
    </div>
  );
}
